import type { InputMaybe } from "@/lib/gql/__generated__/graphql";
import { BillingTypeTargetForOption, OptionType } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types";

import type { FormOptionType } from "@/components/ui/form/_types/type";

type Props = {
	/**
	 * オプションのタイプ（サービス なのか グッズ なのか）
	 */
	optionType: ForNodeType<OptionType>; //
	/**
	 * 〜あたり（料金）
	 * １人当たり、１部屋当たりなど
	 */
	billingTypeTarget: ForNodeType<BillingTypeTargetForOption>; //
	/**
	 * 購入可能上限
	 */
	maxBuyableNumPerTarget: ForNodeType<InputMaybe<number> | undefined> | undefined;

	onSetViewModel: (
		values: {
			key: "optionType" | "billingTypeTarget" | "maxBuyableNumPerTarget";
			value:
				| ForNodeType<OptionType>
				| ForNodeType<BillingTypeTargetForOption>
				| ForNodeType<InputMaybe<number> | undefined>;
		}[],
	) => void;
};

export const useOptionSalesType = ({
	optionType,
	billingTypeTarget,
	maxBuyableNumPerTarget,
	onSetViewModel,
}: Props) => {
	const optionTypeOptions = [
		{
			id: "option_optionType_0",
			label: "サービス",
			value: OptionType.Service,
		},
		{
			id: "option_optionType_1",
			label: "グッズ",
			value: OptionType.Goods,
		},
	] satisfies FormOptionType[];

	const onSetOptionType = (input: string) => {
		onSetViewModel([
			{
				key: "optionType",
				value: {
					...optionType,
					value: input as OptionType,
				},
			},
		]);
	};

	const shouldShowBillingTypeTarget = optionType.value === OptionType.Service;

	const billingTypeTargetOptions = [
		{
			id: "option_billingTypeTarget_0",
			label: "１人あたり",
			value: BillingTypeTargetForOption.Person,
		},
		{
			id: "option_billingTypeTarget_1",
			label: "１部屋あたり",
			value: BillingTypeTargetForOption.Room,
		},
		{
			id: "option_billingTypeTarget_2",
			label: "１団体あたり",
			value: BillingTypeTargetForOption.Group,
		},
		{
			id: "option_billingTypeTarget_3",
			label: "子ども１人あたり",
			value: BillingTypeTargetForOption.PersonOnlyKids,
		},
	] satisfies FormOptionType[];

	const onSetBillingTypeTarget = (input: string) => {
		onSetViewModel([
			{
				key: "billingTypeTarget",
				value: {
					...billingTypeTarget,
					value: input as BillingTypeTargetForOption,
				},
			},
		]);
	};

	const onSetMaxBuyableNumPerTarget = (input: number | undefined) => {
		onSetViewModel([
			{
				key: "maxBuyableNumPerTarget",
				value: {
					isEdited: true,
					value: input!,
				},
			},
		]);
	};

	const isEdited = optionType.isEdited || billingTypeTarget.isEdited || (maxBuyableNumPerTarget?.isEdited ?? false);
	const hasError = Boolean(optionType.error || billingTypeTarget.error || maxBuyableNumPerTarget?.error || false);

	return {
		isEdited,
		hasError,

		optionType,
		onSetOptionType,
		optionTypeOptions,

		shouldShowBillingTypeTarget,
		billingTypeTarget,
		onSetBillingTypeTarget,
		billingTypeTargetOptions,

		maxBuyableNumPerTarget,
		onSetMaxBuyableNumPerTarget,
	};
};
